import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Image as ImageIcon } from 'lucide-react';
import { useSignedUrl } from '@/hooks/useSignedUrl';

interface EntityImageProps {
  imagePath?: string | null;
  alt: string;
  className?: string;
  variant?: 'circular' | 'square';
}

/**
 * Component to display entity images with signed URLs
 * Handles loading signed URLs from private storage with caching
 */
export function EntityImage({ imagePath, alt, className, variant = 'square' }: EntityImageProps) {
  const { data: signedUrl, isLoading: loading, isError } = useSignedUrl(imagePath);
  const [imageError, setImageError] = useState(false);

  const error = isError || imageError;

  if (!imagePath || error) {
    return (
      <div
        className={cn(
          'bg-muted flex items-center justify-center',
          variant === 'circular' ? 'rounded-full' : 'rounded-lg',
          className
        )}
      >
        <ImageIcon className="h-1/2 w-1/2 text-muted-foreground" />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={cn(
          'bg-muted animate-pulse',
          variant === 'circular' ? 'rounded-full' : 'rounded-lg',
          className
        )}
      />
    );
  }

  return (
    <img
      src={signedUrl || undefined}
      alt={alt}
      className={cn(
        'object-cover',
        variant === 'circular' ? 'rounded-full' : 'rounded-lg',
        className
      )}
      onError={() => setImageError(true)}
    />
  );
}
